/**
 * @file RoleEditorModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-27
 * @modified 2026-08-27
 *
 * Create or edit a role: its name, the badge it confers, whether its holders
 * must have Xero connected, and the permissions it grants.
 *
 * Two things are locked on a built-in role, and the modal says why rather than
 * silently disabling a control:
 *   - the badge, because Sales Rep IS the REP badge and changing it would take
 *     every rep out of view-only mode without anyone touching a permission
 *   - deletion, handled by the caller
 * The permission SET stays editable on every role, built-in included — that is
 * what "roles which can then be customised" means.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import PermissionPicker from './PermissionPicker';
import { useSlideOutButton } from './SlideOutButton';

const BADGE_OPTIONS = [
  {
    key: 'USER',
    label: 'User',
    blurb: 'Standard staff access. Several User roles can be held at once without changing the badge.'
  },
  {
    key: 'REP',
    label: 'Rep',
    blurb: 'Scoped to one sales rep’s accounts and view-only everywhere except the Blend Calculator. Cannot connect to Xero.'
  },
  {
    key: 'ADMIN',
    label: 'Admin',
    blurb: 'Holds every permission, present and future, and can manage users and roles.'
  }
];

function RoleEditorBody({
  form, setForm, categories, permissions, isSystem, saving
}) {
  useSlideOutButton([saving]);

  const selected = useMemo(() => new Set(form.permissions), [form.permissions]);

  const toggle = (key) => {
    setForm(prev => {
      const next = new Set(prev.permissions);
      if (next.has(key)) next.delete(key); else next.add(key);
      return { ...prev, permissions: [...next] };
    });
  };

  const setMany = (keys, on) => {
    setForm(prev => {
      const next = new Set(prev.permissions);
      for (const k of keys) { if (on) next.add(k); else next.delete(k); }
      return { ...prev, permissions: [...next] };
    });
  };

  return (
    <div className="role-editor">
      <div className="role-editor-fields">
        <label className="field">
          <span className="field-label">Name</span>
          <Input
            value={form.name}
            onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Order Desk"
            disabled={saving}
          />
        </label>

        <label className="field">
          <span className="field-label">Description</span>
          <textarea
            className="styled-textarea"
            rows={2}
            value={form.description || ''}
            onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="What this role is for, and who should have it."
            disabled={saving}
          />
        </label>
      </div>

      <div className="field">
        <span className="field-label">Badge</span>
        {isSystem && (
          <p className="field-note">
            This is a built-in role and its badge is fixed. The badge decides
            view-only mode and Xero eligibility, so changing it here would alter
            how every holder is treated without any permission having moved.
          </p>
        )}
        <div className="badge-options">
          {BADGE_OPTIONS.map(opt => (
            <button
              type="button"
              key={opt.key}
              className={`badge-option${form.badge === opt.key ? ' selected' : ''}`}
              disabled={saving || isSystem}
              onClick={() => setForm(p => ({
                ...p,
                badge: opt.key,
                // A rep never connects Xero, so the toggle below is meaningless
                // for them — clear it rather than leaving a setting that lies.
                requiresXero: opt.key === 'REP' ? false : p.requiresXero
              }))}
            >
              <span className={`status-badge badge-${opt.key.toLowerCase()}`}>{opt.label}</span>
              <span className="badge-option-blurb">{opt.blurb}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <span className="field-label">Xero</span>
        {form.badge === 'REP' ? (
          <p className="field-note">
            Rep accounts cannot connect to Xero and are never prompted to. They
            read what has already been synced into the system.
          </p>
        ) : (
          <div className="checkbox-container xero-required-toggle">
            <div className="check light">
              <input
                id="role-requires-xero"
                type="checkbox"
                checked={form.requiresXero !== false}
                disabled={saving}
                onChange={(e) => setForm(p => ({ ...p, requiresXero: e.target.checked }))}
              />
              <label htmlFor="role-requires-xero"></label>
            </div>
            <span className="toggle-caption">
              Holders must have Xero connected. They are blocked with a prompt
              whenever the connection is missing or has expired.
            </span>
          </div>
        )}
      </div>

      <div className="field">
        <span className="field-label">
          Permissions <span className="field-count">{form.permissions.length} selected</span>
        </span>
        <PermissionPicker
          mode="binary"
          categories={categories}
          permissions={permissions}
          selected={selected}
          onToggle={toggle}
          onSetMany={setMany}
          disabled={saving}
        />
      </div>
    </div>
  );
}

function RoleEditorModal({ isOpen, role, categories, permissions, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: '', description: '', badge: 'USER', requiresXero: true, permissions: []
  });

  useEffect(() => {
    if (!isOpen) return;
    setForm(role
      ? {
          name: role.name || '',
          description: role.description || '',
          badge: role.badge || 'USER',
          requiresXero: role.requires_xero !== false,
          permissions: [...(role.permissions || [])]
        }
      : { name: '', description: '', badge: 'USER', requiresXero: true, permissions: [] });
  }, [isOpen, role]);

  const isSystem = role?.is_system === true;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={role ? `Edit Role — ${role.name}` : 'New Role'}
      size="lg"
      className="role-editor-modal"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button
              className="btn-slideout"
              onClick={() => onSave(form)}
              disabled={saving || !form.name.trim()}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{saving ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </div>
      }
    >
      {isOpen && (
        <RoleEditorBody
          form={form}
          setForm={setForm}
          categories={categories}
          permissions={permissions}
          isSystem={isSystem}
          saving={saving}
        />
      )}
    </Modal>
  );
}

export default RoleEditorModal;
